/**
 * The CallbackManager class manages event callbacks, allowing for registration,
 * deregistration, and emission of events
 */
class CallbackManager {
	constructor() {
		// Initialize an object to store callbacks. Each key (event ID) will map to an array of callback functions
		this.callbacks = {};
	}

	/**
	 * Registers a callback function for a given event ID
	 *
	 * @param {string} id - The event ID to listen for
	 * @param {function} callbackFunction - The callback function to register
	 */
	on(id, callbackFunction) {
		// If no callbacks are registered for the event ID, initialize an empty array
		if (!this.callbacks[id]) {
			this.callbacks[id] = [];
		}
		this.callbacks[id].push(callbackFunction);
	}

	/**
	 * Removes a callback function for a given event ID
	 *
	 * @param {string} id - The event ID to remove the callback from
	 * @param {function} callbackFunction - The callback function to remove
	 */
	off(id, callbackFunction) {
		// Check if any callbacks are registered for the event ID
		if (this.callbacks[id]) {
			this.callbacks[id] = this.callbacks[id].filter(cb => cb !== callbackFunction);
		}
	}

	/**
	 * Emits an event, causing all registered callbacks for this event ID to be called
	 *
	 * @param {string} id - The event ID to emit
	 * @param {...any} args - Arguments to pass to the callback functions
	 */
	emit(id, ...args) {
		if (this.callbacks[id] && this.callbacks[id].length > 0) {
			// Assuming there's only one callback per event ID
			try {
				return this.callbacks[id][0](...args);
			} catch (error) {
				// eslint-disable-next-line no-console
				return console.error(`Error in callback for ${id}:`, error);
			}
		}
	}

	/**
	 * Checks if any callbacks are registered for a given event ID
	 *
	 * @param {string} id - The event ID to check
	 * @returns {boolean} - True if callbacks are registered, false otherwise
	 */
	isRegistered(id) {
		return !!this.callbacks[id] && this.callbacks[id].length > 0;
	}
}

export default CallbackManager;
